import React, { useState, useEffect } from 'react';
import AceEditor from 'react-ace';
import ReactMarkdown from 'react-markdown';
import SyntaxHighlighter from "react-syntax-highlighter";
import { irBlack } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/theme-tomorrow_night_bright';
import 'ace-builds/src-noconflict/ext-language_tools';
import './TaskModal.css';

// Helper copy function
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {}
};

function CodeBlock({ language, value }) {
  return (
    <div className="code-block-container">
      <div className="code-block-header">
        <span className="code-block-language">{language}</span>
        <button
          className="code-block-copy"
          onClick={() => copyToClipboard(value)}
        >
          Копировать код
        </button>
      </div>
      <SyntaxHighlighter
        language={language}
        style={irBlack}
        wrapLongLines
        customStyle={{ margin: 0, padding: '12px' }}
      >
        {value}
      </SyntaxHighlighter>
    </div>
  );
}

export default function TaskModal({
  taskData,
  loading,
  initialCode,
  onClose,
  courseName,
  userId,
}) {
  const [code, setCode] = useState('# Пиши свой код сюда');
  const [submitting, setSubmitting] = useState(false);
  const [checkError, setCheckError] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [paiResponse, setPaiResponse] = useState("");

  useEffect(() => {
    setCode('# Пиши свой код сюда');
    setIsCorrect(null);
    setPaiResponse("");
  }, [taskData, initialCode]);

  const handleEditorChange = (newValue) => {
    setCode(newValue);
  };

  const handleCheckSolution = async () => {
    setSubmitting(true);
    setCheckError(null);
    setIsCorrect(null);
    setPaiResponse("");

    try {
      const response = await fetch("/api/tasks/check", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          topic:             taskData.topic,
          task_number:       taskData.task_number,
          task_progress_id:  taskData.task_progress_id,
          answer:            code,
          course_name:       courseName,
          user_id:           userId,
        }),
      });

      if (!response.ok) {
        const txt = await response.text();
        throw new Error(`Ошибка ${response.status}: ${txt}`);
      }

      const data = await response.json();
      if (typeof data.is_correct !== "boolean" || typeof data.ai_response !== "string") {
        throw new Error("Непредвиденный формат ответа");
      }

      setPaiResponse(data.ai_response);
      setIsCorrect(data.is_correct);
    } catch (err) {
      console.error(err);
      setCheckError(err.message || "Произошла ошибка при проверке.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeFeedback = () => {
    setIsCorrect(null);
    setCheckError(null);
    setPaiResponse("");
  };

  return (
    <div className="task-modal-overlay" onClick={onClose}>
      <div className="task-modal-content" onClick={e => e.stopPropagation()}>
        <button className="task-modal-close" onClick={onClose}>✖</button>

        {loading ? (
          <div className="task-modal-loading">Загрузка задачи...</div>
        ) : !taskData ? (
          <div className="task-modal-no-task">Нет задачи для отображения.</div>
        ) : (
          <div className="task-modal-body">
            {/* Left Pane */}
            <div className="task-modal-description">
              <h2 className="task-modal-title">{taskData.title}</h2>
              <div className="task-modal-text">
                <ReactMarkdown
                  components={{
                    code({ inline, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || '');
                      if (!inline && match) {
                        return (
                          <CodeBlock
                            language={match[1]}
                            value={String(children).replace(/\n$/, '')}
                          />
                        );
                      }
                      return <code className={className} {...props}>{children}</code>;
                    }
                  }}
                >
                  {taskData.description}
                </ReactMarkdown>
              </div>
              {/* Example code display */}
              {taskData.code && (
                <div className="task-modal-example-code">
                  <h3>Пример кода:</h3>
                  <SyntaxHighlighter
                    language="python"
                    style={irBlack}
                    wrapLongLines
                    customStyle={{ margin: 0, padding: '12px', borderRadius: '12px' }}
                  >
                    {taskData.code}
                  </SyntaxHighlighter>
                </div>
              )}
            </div>

            {/* Right Pane */}
            <div className="task-modal-editor">
              <AceEditor
                mode="python"
                theme="tomorrow_night_bright"
                name="task_modal_editor"
                onChange={handleEditorChange}
                value={code}
                fontSize={14}
                showPrintMargin={false}
                showGutter
                highlightActiveLine
                width="100%"
                height="100%"
                setOptions={{
                  enableBasicAutocompletion: true,
                  enableLiveAutocompletion:  true,
                  enableSnippets:            true,
                  showLineNumbers:           true,
                  tabSize:                   2,
                }}
              />
              <div className="task-modal-controls">
                <button
                  className="task-modal-submit"
                  onClick={handleCheckSolution}
                  disabled={submitting}
                >
                  {submitting ? "Проверка..." : "Отправить код на проверку"}
                </button>

                {checkError && (
                  <div className="error-message">
                    {checkError}
                    <button className="close-error" onClick={closeFeedback}>✖</button>
                  </div>
                )}

                {isCorrect != null && (
                  <div className={`feedback-block ${isCorrect ? "feedback-correct" : "feedback-wrong"}`}>
                    <div className="feedback-header">
                      {isCorrect ? "Правильно!" : "Не совсем..."}
                      <button className="close-feedback" onClick={closeFeedback}>✖</button>
                    </div>
                    <div className="feedback-body">
                      {paiResponse}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
